import { useEffect } from 'react';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { AccordionItem } from './accordion-container';

interface FAQSchema {
  '@context': string;
  '@type': string;
  mainEntity: Array<{
    '@type': string;
    name: string;
    acceptedAnswer: {
      '@type': string;
      text: string;
    };
  }>;
}

export const useFAQSchema = (isFAQ: boolean, accordions: AccordionItem[]) => {
  useEffect(() => {
    if (!isFAQ || !accordions?.length) {
      return;
    }

    const faqItems = accordions
      .map((item) => {
        const heading = item.heading;
        if (!heading || !item.content?.length) {
          return null;
        }

        try {
          // Extract text from the first content item
          const textDocument = item.content[0]?.fields?.text;
          if (!textDocument) {
            return null;
          }

          const text = documentToPlainTextString(textDocument);
          return { heading, text };
        } catch (error) {
          console.warn('Error processing FAQ content:', error);
          return null;
        }
      })
      .filter(
        (item): item is { heading: string; text: string } => item !== null
      );

    if (!faqItems.length) {
      return;
    }

    const schema: FAQSchema = {
      '@context': 'https://schema.org/',
      '@type': 'FAQPage',
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.heading,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.text
        }
      }))
    };

    // Create script element
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    script.id = 'faq-schema-script';

    // Remove existing FAQ schema script if it exists
    const existingScript = document.getElementById('faq-schema-script');
    if (existingScript) {
      existingScript.remove();
    }

    // Add to head
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      const scriptToRemove = document.getElementById('faq-schema-script');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [isFAQ, accordions]);
};
